import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  getCurrentUser,
  getSavedListings,
  getSavedSearches,
  listSessionSavedListings,
  listSessionSavedSearches,
  login,
  logout,
  signup,
} from "../api/publicClientViewApi";
import { mapSdkError } from "../api/sdkClient";
import { useLocale } from "../../intl";
import {
  addLikedVehicleId,
  BUYER_LIKES_CHANGED_EVENT,
  loadLikedVehicleIds,
  removeLikedVehicleId,
} from "../../utils/automap/buyerLikesStorage";
import { buildGuestMergePayload, readPendingGuestMerge } from "../../utils/automap/buyerGuestMergeStorage";
import { runBuyerGuestMerge } from "../../utils/automap/buyerGuestMergeService";
import { useClientView } from "./ClientViewProvider";
import { CASAMAPA_CLIENT_VIEW_SLUG } from "../utils/casaMapaManifestEnrichment";

const PublicAccountContext = createContext(null);

export function PublicAccountProvider({ children }) {
  const { slug, visitorId, sessionId, isPreview, saveSearchEnabled, status: viewStatus } = useClientView();
  const effectiveSaveSearchEnabled =
    saveSearchEnabled || slug === CASAMAPA_CLIENT_VIEW_SLUG;
  const { t } = useLocale();
  const [user, setUser] = useState(null);
  const [authStatus, setAuthStatus] = useState("unknown");
  const [savedListingIds, setSavedListingIds] = useState(() => new Set());
  const [savedSearches, setSavedSearches] = useState([]);
  const [flash, setFlash] = useState(null);
  const [mergeNotice, setMergeNotice] = useState(null);
  const [mergeWarning, setMergeWarning] = useState(null);
  const pendingMergeHandledRef = useRef(false);

  const mergeSavedListingIds = useCallback((apiIds = []) => {
    const mirrorIds = loadLikedVehicleIds(slug);
    return new Set([...apiIds, ...mirrorIds].filter(Boolean));
  }, [slug]);

  const refreshSavedState = useCallback(async () => {
    if (!slug || isPreview) {
      return;
    }

    if (!effectiveSaveSearchEnabled) {
      setSavedSearches([]);
    }

    try {
      const searchesPromise = effectiveSaveSearchEnabled
        ? user
          ? getSavedSearches(slug)
          : visitorId
            ? listSessionSavedSearches(slug, visitorId)
            : Promise.resolve({ savedSearches: [] })
        : Promise.resolve({ savedSearches: [] });

      if (user) {
        const [listingsBody, searchesBody] = await Promise.all([
          getSavedListings(slug),
          searchesPromise,
        ]);
        const ids = (listingsBody.savedListings || []).map(
          (item) => item.canonicalListingId || item.listingSnapshotJson?.id || item.listingSnapshot?.id
        );
        setSavedListingIds(mergeSavedListingIds(ids));
        setSavedSearches(searchesBody.savedSearches || []);
      } else if (visitorId) {
        const [listingsBody, searchesBody] = await Promise.all([
          listSessionSavedListings(slug, visitorId),
          searchesPromise,
        ]);
        const ids = (listingsBody.savedListings || []).map(
          (item) => item.canonicalListingId || item.listingSnapshotJson?.id || item.listingSnapshot?.id
        );
        setSavedListingIds(mergeSavedListingIds(ids));
        setSavedSearches(searchesBody.savedSearches || []);
      } else {
        setSavedListingIds(new Set(loadLikedVehicleIds(slug)));
        setSavedSearches([]);
      }
    } catch {
      setSavedListingIds((prev) => {
        const mirrorIds = loadLikedVehicleIds(slug);
        return mirrorIds.length ? mergeSavedListingIds([...prev, ...mirrorIds]) : prev;
      });
    }
  }, [slug, user, visitorId, isPreview, effectiveSaveSearchEnabled, mergeSavedListingIds]);

  const refreshUser = useCallback(async () => {
    if (!slug || isPreview) {
      setUser(null);
      setAuthStatus("guest");
      return;
    }

    setAuthStatus("loading");
    try {
      const body = await getCurrentUser(slug);
      setUser(body.user || null);
      setAuthStatus(body.user ? "authenticated" : "guest");
    } catch {
      setUser(null);
      setAuthStatus("guest");
    }
  }, [slug, isPreview]);

  const showFlash = useCallback((message, tone = "success") => {
    setFlash({ message, tone });
    window.setTimeout(() => setFlash(null), 4000);
  }, []);

  const applyGuestMergeResult = useCallback(
    async (authMethod) => {
      const result = await runBuyerGuestMerge({
        slug,
        visitorId,
        authMethod,
        t,
      });

      if (result.ok) {
        pendingMergeHandledRef.current = true;
        if (result.toastMessage) {
          setMergeNotice(result.toastMessage);
        }
        if (result.recoveryWarning) {
          setMergeWarning(result.recoveryWarning);
        }
        await refreshSavedState();
      } else if (result.errorMessage) {
        showFlash(result.errorMessage, "error");
      }

      return result;
    },
    [slug, visitorId, t, refreshSavedState, showFlash]
  );

  useEffect(() => {
    if (viewStatus !== "ready" || isPreview) {
      return;
    }
    refreshUser();
  }, [viewStatus, isPreview, refreshUser]);

  useEffect(() => {
    if (viewStatus !== "ready" || isPreview) {
      return;
    }
    refreshSavedState();
  }, [viewStatus, isPreview, user, refreshSavedState]);

  useEffect(() => {
    if (viewStatus !== "ready" || isPreview || !slug) {
      return;
    }
    setSavedListingIds((prev) => mergeSavedListingIds([...prev]));
  }, [viewStatus, isPreview, slug, mergeSavedListingIds]);

  useEffect(() => {
    if (!slug || isPreview) return undefined;
    const onMirrorChanged = (event) => {
      if (event.detail?.slug !== slug) return;
      setSavedListingIds((prev) => {
        const next = new Set(prev);
        const listingId = event.detail?.listingId;
        if (!listingId) return next;
        if (event.detail.liked) next.add(listingId);
        else next.delete(listingId);
        return next;
      });
    };
    window.addEventListener(BUYER_LIKES_CHANGED_EVENT, onMirrorChanged);
    return () => window.removeEventListener(BUYER_LIKES_CHANGED_EVENT, onMirrorChanged);
  }, [slug, isPreview]);

  useEffect(() => {
    if (
      viewStatus !== "ready" ||
      isPreview ||
      authStatus !== "authenticated" ||
      !user ||
      pendingMergeHandledRef.current
    ) {
      return;
    }

    const pending = readPendingGuestMerge();
    if (!pending || pending.clientViewSlug !== slug) {
      return;
    }

    applyGuestMergeResult("google");
  }, [viewStatus, isPreview, authStatus, user, slug, applyGuestMergeResult]);

  const handleSignup = useCallback(
    async (form) => {
      const guestPayload = buildGuestMergePayload(slug, visitorId).guestPayload;
      const body = await signup(slug, {
        ...form,
        visitorId,
        sessionId,
        guestPayload,
      });
      setUser(body.user);
      setAuthStatus("authenticated");
      await applyGuestMergeResult("signup");
      return body;
    },
    [slug, visitorId, sessionId, applyGuestMergeResult]
  );

  const handleLogin = useCallback(
    async (form) => {
      const guestPayload = buildGuestMergePayload(slug, visitorId).guestPayload;
      let recovery = null;
      try {
        const { readChatAuthRecoveryContext, clearChatAuthRecoveryContext } = await import(
          "../../utils/automap/chatAuthRecoveryContext.js"
        );
        recovery = readChatAuthRecoveryContext();
        const body = await login(slug, {
          ...form,
          visitorId,
          sessionId,
          guestPayload,
          anonymousSessionId: form.anonymousSessionId || recovery?.anonymousSessionId || null,
          challengeId: form.challengeId || recovery?.challengeId || null,
          source: form.source || "standard_signin",
          locale: form.locale || undefined,
        });
        setUser(body.user);
        setAuthStatus("authenticated");
        await applyGuestMergeResult("email");
        if (body.conversationClaim) {
          clearChatAuthRecoveryContext();
        }
        return body;
      } catch (error) {
        throw error;
      }
    },
    [slug, visitorId, sessionId, applyGuestMergeResult]
  );

  const handleLogout = useCallback(async () => {
    await logout(slug);
    setUser(null);
    setAuthStatus("guest");
    pendingMergeHandledRef.current = false;
    setMergeWarning(null);
    await refreshSavedState();
  }, [slug, refreshSavedState]);

  const isListingSaved = useCallback(
    (listingId) => savedListingIds.has(listingId),
    [savedListingIds]
  );

  const markListingSaved = useCallback((listingId) => {
    if (slug) addLikedVehicleId(slug, listingId);
    setSavedListingIds((prev) => new Set(prev).add(listingId));
  }, [slug]);

  const markListingUnsaved = useCallback((listingId) => {
    if (slug) removeLikedVehicleId(slug, listingId);
    setSavedListingIds((prev) => {
      const next = new Set(prev);
      next.delete(listingId);
      return next;
    });
  }, [slug]);

  const value = useMemo(
    () => ({
      user,
      authStatus,
      isAuthenticated: Boolean(user),
      savedListingIds,
      savedSearches,
      flash,
      mergeNotice,
      mergeWarning,
      clearMergeNotice: () => setMergeNotice(null),
      clearMergeWarning: () => setMergeWarning(null),
      refreshUser,
      refreshSavedState,
      signup: handleSignup,
      login: handleLogin,
      logout: handleLogout,
      isListingSaved,
      markListingSaved,
      markListingUnsaved,
      showFlash,
      mapAuthError: mapSdkError,
    }),
    [
      user,
      authStatus,
      savedListingIds,
      savedSearches,
      flash,
      mergeNotice,
      mergeWarning,
      refreshUser,
      refreshSavedState,
      handleSignup,
      handleLogin,
      handleLogout,
      isListingSaved,
      markListingSaved,
      markListingUnsaved,
      showFlash,
    ]
  );

  return (
    <PublicAccountContext.Provider value={value}>{children}</PublicAccountContext.Provider>
  );
}

export function usePublicAccount() {
  const context = useContext(PublicAccountContext);
  if (!context) {
    throw new Error("usePublicAccount must be used within PublicAccountProvider");
  }
  return context;
}

/** Soft account hook for chat/widget surfaces that may render without the provider in tests. */
export function useOptionalPublicAccount() {
  const context = useContext(PublicAccountContext);
  if (!context) {
    return {
      isAuthenticated: false,
      authStatus: "guest",
      user: null,
    };
  }
  return context;
}
